import { useSelector, useDispatch } from "react-redux";
import { Grid, Button, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useLazyGetUserCompletedCoursesQuery, useLazyGetUserOutstandingCoursesQuery } from "../../store/services/response.service";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { APP_URL } from "../../config";
import { useLazyGetCourseByIdQuery } from "../../store/services/university-service";
import { useGetUserByIdQuery } from "../../store/services/user-service";
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import { useDynamicBreakpoints } from '../../utils/breakpointUtils';
import YourCourses from "./components/YourCourses";
import UserViewYourCourses from "./components/user-view/UserViewYourCourses";
import SimulationPerformance from "./components/SimulationPerformance";
import UserSearch from './components/UserSearch';
import useSimulationData from './use-data-hooks/useSimulationData';
import useUserSearch from './use-data-hooks/useUserSearch';
import ActivityTimeline from './components/ActivityTimeline';
import UserViewActivityTimeline from './components/user-view/UserViewActivityTimeline';
import {
    useGetTenancyBasicsQuery,
    useGetTenancyByIdQuery,
    useGetFeatureSwitchesQuery
} from "../../store/services/tenancy.service";
import DepartmentOverview from './components/DepartmentOverview';
import UserViewDepartmentOverview from './components/user-view/UserViewDepartmentOverview';
import UserViewLocationOverview from './components/user-view/UserViewLocationOverview';
import UserViewLineManagerOverview from './components/user-view/UserViewLineManagerOverview';

export default function Dashboard() {
    const theme = useTheme();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { matchDownSM, matchDownMD, matchDownLG } = useDynamicBreakpoints(drawerOpen);
    const { user } = useSelector((state) => state.auth);
    const { tenancyId } = useSelector((state) => state.tenancy);

    const userLoggedIn = JSON.parse(localStorage.getItem("user"));
    const userRole = userLoggedIn?.role || '';
    const isAdmin = userRole === 'Administrators' || userRole.includes('TenancyAdministrators');

    // Get full user details including reportingLevel
    const userId = userLoggedIn?.id;
    const { data: fullUserData, isLoading: userDetailsLoading, error: userDetailsError } = useGetUserByIdQuery(
        { userId },
        { skip: !userId }
    );

    // Determine user reporting levels
    const userReportingLevel = fullUserData?.reportingLevel || userLoggedIn?.reportingLevel || '';
    const userHasDepartmentReporting = userReportingLevel === 'Department';
    const userHasLocationReporting = userReportingLevel === 'Location';
    const userHasLineManagerReporting = userReportingLevel === 'Line manager' || userReportingLevel === 'LineManager';

    // Reliable tenancy detection - checks all sources in priority order
    const getActiveTenancyId = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlTenancyId = urlParams.get('tenancyId');

        if (urlTenancyId && urlTenancyId !== 'undefined') {
            return parseInt(urlTenancyId, 10);
        }

        if (tenancyId) {
            return parseInt(tenancyId, 10);
        }

        return userLoggedIn?.tenancyId;
    };

    const activeTenancyId = getActiveTenancyId();

    // API queries - simplified skip conditions
    const { data: tenancyBasics } = useGetTenancyBasicsQuery(
        { tenancyId: activeTenancyId },
        { skip: !activeTenancyId }
    );

    const { data: tenancyData } = useGetTenancyByIdQuery(
        { tenancyId: activeTenancyId },
        { skip: !activeTenancyId }
    );

    const { data: featureSwitchesData } = useGetFeatureSwitchesQuery(
        { tenancyId: activeTenancyId },
        { skip: !activeTenancyId }
    );

    // Helper function to check feature flags
    const isFeatureEnabled = (featureKey) => {
        if (!featureSwitchesData) return true;
        const actualData = featureSwitchesData?.data || featureSwitchesData;
        return actualData[featureKey] !== false;
    };

    // Feature switches
    const isCourseOverviewEnabled = isFeatureEnabled('enableDashboardCourseOverview');
    const isActivityTimelineEnabled = isFeatureEnabled('enableDashboardActivityTimeline');
    const isUserSearchEnabled = isFeatureEnabled('enableDashboardUserSearch');
    const isSimulationPerformanceEnabled = isFeatureEnabled('enableDashboardMostClickedSimulations');
    const isDepartmentOverviewEnabled = isFeatureEnabled('enableDashboardDepartmentOverview');

    // Course display state
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [courseClicked, setCourseClicked] = useState(false);
    const [courseClickedData, setCourseClickedData] = useState(null);

    // API queries
    const [getUserCompletedCourses, {
        data: fetchedUserCompletedCourses,
        error: userCompletedCoursesError,
        isLoading: userCompletedCoursesLoading
    }] = useLazyGetUserCompletedCoursesQuery();

    const [getUserOutstandingCourses, {
        data: userOutstandingCourses,
        error: userOutstandingCoursesError,
        isLoading: userOutstandingCoursesLoading
    }] = useLazyGetUserOutstandingCoursesQuery();

    const [getCourseById] = useLazyGetCourseByIdQuery();

    // Custom hooks - simplified tenancy ID passing
    const {
        searchQuery,
        anchorEl,
        setAnchorEl,
        filteredUsers,
        isSearching,
        userData,
        userDataLoading,
        selectedUserId,
        handleSearchChange,
        handleSelectUser,
        handleClickAway,
        dateRange,
        setDateRange,
        refetchUserData
    } = useUserSearch(isAdmin ? activeTenancyId : null);

    const {
        simulationData,
        simulationDataLoading,
        dateRange: simDateRange,
        setDateRange: setSimDateRange
    } = useSimulationData(isAdmin ? activeTenancyId : null);

    // Count enabled components for dynamic grid sizing
    const enabledComponents = [
        isCourseOverviewEnabled,
        isActivityTimelineEnabled,
        isAdmin && isUserSearchEnabled,
        isAdmin && isSimulationPerformanceEnabled,
        userHasDepartmentReporting && isDepartmentOverviewEnabled,
        userHasLineManagerReporting && isDepartmentOverviewEnabled
    ].filter(Boolean).length;

    const shouldUseFullHeight = () => {
        return !isAdmin &&
            !(isDepartmentOverviewEnabled && (userHasDepartmentReporting || userHasLocationReporting || userHasLineManagerReporting));
    };

    // Dynamic grid sizing
    const getGridSizes = () => {
        if (matchDownSM) {
            return { default: 12 };
        } else if (enabledComponents <= 2) {
            return {
                courseOverview: 8,
                activityTimeline: 4,
                default: 6
            };
        } else if (matchDownMD) {
            return { default: 6 };
        } else {
            return {
                courseOverview: 8,
                activityTimeline: 4,
                analytics: 6,
                performance: 6,
                departmentOverview: 12
            };
        }
    };

    const gridSizes = getGridSizes();

    // Authentication check
    useEffect(() => {
        if (!user || !user.email) {
            console.log("user undefined in redux state, redirecting to login...");
            window.location.href = "/login";
        }
    }, [user]);

    // Load user courses
    useEffect(() => {
        if (user) {
            getUserCompletedCourses({ userEmail: user.email });
            getUserOutstandingCourses({ userEmail: user.email });
            setIsDataLoaded(true);
        }
    }, [user, getUserCompletedCourses, getUserOutstandingCourses]);

    // Course content handling
    const showCourseContent = async (course) => {
        try {
            // Explicitly encode to ensure consistent behavior across browsers
            const encodedCourseId = encodeURIComponent(course.universityCourseId);
            const r = await getCourseById({ courseId: encodedCourseId, includes: "" }).unwrap();
            if (r.status === "rejected") {
                throw new Error("Failed to fetch course content");
            } else {
                setCourseClickedData(r);
                setCourseClicked(true);
                window.scrollTo(0, document.body.scrollHeight);
            }
        } catch (e) {
            console.log("Caught error:", e);
        }
    };

    const handleCloseCourseContent = () => {
        setCourseClicked(false);
        setCourseClickedData(null);
        window.location.reload();
    };

    // Show loading if no tenancy ID available or user details are loading
    if (!activeTenancyId || userDetailsLoading) {
        return (
            <Grid
                container
                spacing={3}
                sx={{
                    pt: 3,
                    minHeight: 'calc(100vh - 200px)',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Grid item xs={12} md={6}>
                    <MainCard border={false} boxShadow={false} sx={{ bgcolor: 'transparent' }}>
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <CircularProgress size={100} sx={{ mb: 2 }} />
                            <h3>Loading Dashboard...</h3>
                            <p>{userDetailsLoading ? 'Loading user permissions...' : 'Loading tenancy data...'}</p>
                        </div>
                    </MainCard>
                </Grid>
            </Grid>
        );
    }

    // Handle user details error
    if (userDetailsError) {
        console.error('Error loading user details:', userDetailsError);
        // Continue with default permissions if user details fail to load
    }

    // Render course content view
    if (courseClicked && courseClickedData) {
        const parentOrigin = window.location.origin;
        const isLmsCourse = courseClickedData.isLms === true;

        // Generate appropriate course URL
        const getCourseUrl = () => {
            if (isLmsCourse) {
                // For LMS courses, use the SCORM wrapper
                const absoluteCourseUrl = `${APP_URL}/${courseClickedData.courseContentPath}`;
                return `/scorm-wrapper.html?` +
                    `courseUrl=${encodeURIComponent(absoluteCourseUrl)}&` +
                    `emailAddress=${encodeURIComponent(user.email)}&` +
                    `courseIdentifier=${encodeURIComponent(courseClickedData.courseName)}`;
            } else {
                // For standard courses, use the direct URL
                return `${APP_URL}/${courseClickedData.courseContentPath}?useremailaddress=${user.email}&courseidentifier=${encodeURIComponent(courseClickedData.courseName)}&parentorigin=${encodeURIComponent(parentOrigin)}`;
            }
        };

        return (
            <>
                <Grid container justifyContent="space-between">
                    <Button onClick={handleCloseCourseContent}
                        variant="contained"
                        startIcon={<KeyboardArrowLeftTwoToneIcon />}
                        sx={{ mb: 2 }}
                    >
                        Back
                    </Button>
                </Grid>

                <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                    <iframe
                        style={{ width: "100%", height: "80vh", border: "none" }}
                        title={`course`}
                        src={getCourseUrl()}
                        allow={isLmsCourse ? "autoplay; fullscreen" : undefined}
                        allowFullScreen={isLmsCourse}
                    />
                </MainCard>
            </>
        );
    }

    // Main dashboard render
    return (
        <Grid container spacing={matchDownSM ? 2 : 3} sx={{ pt: matchDownSM ? 2 : 3 }}>

            {/* Course Overview - Conditionally rendered */}
            {isAdmin ? (
                <>
                    {isCourseOverviewEnabled && (
                        <Grid item xs={12} md={gridSizes.courseOverview || gridSizes.default}>
                            <YourCourses
                                isDataLoaded={isDataLoaded}
                                userOutstandingCourses={userOutstandingCourses}
                                userOutstandingCoursesLoading={userOutstandingCoursesLoading}
                                userOutstandingCoursesError={userOutstandingCoursesError}
                                showCourseContent={showCourseContent}
                            />
                        </Grid>
                    )}

                    {isActivityTimelineEnabled && (
                        <Grid item xs={12} md={gridSizes.activityTimeline || gridSizes.default}>
                            <ActivityTimeline
                                userOutstandingCourses={userOutstandingCourses}
                                fetchedUserCompletedCourses={fetchedUserCompletedCourses}
                                showCourseContent={showCourseContent}
                                user={user}
                                loading={userCompletedCoursesLoading || userOutstandingCoursesLoading}
                                tenancyBasics={tenancyBasics}
                                tenancyData={tenancyData}
                            />
                        </Grid>
                    )}
                </>
            ) : (
                <>
                    {isCourseOverviewEnabled && (
                       <Grid item xs={12} md={gridSizes.courseOverview || gridSizes.default}>
                            <UserViewYourCourses
                                isDataLoaded={isDataLoaded}
                                userOutstandingCourses={userOutstandingCourses}
                                userOutstandingCoursesLoading={userOutstandingCoursesLoading}
                                userOutstandingCoursesError={userOutstandingCoursesError}
                                showCourseContent={showCourseContent}
                                useFullHeight={shouldUseFullHeight()}
                            />
                        </Grid>
                    )}

                    {isActivityTimelineEnabled && (
                      <Grid item xs={12} md={gridSizes.activityTimeline || gridSizes.default}>
                            <UserViewActivityTimeline
                                userOutstandingCourses={userOutstandingCourses}
                                fetchedUserCompletedCourses={fetchedUserCompletedCourses}
                                showCourseContent={showCourseContent}
                                user={user}
                                loading={userCompletedCoursesLoading || userOutstandingCoursesLoading}
                                tenancyBasics={tenancyBasics}
                                tenancyData={tenancyData}
                                useFullHeight={shouldUseFullHeight()}
                            />
                      </Grid>
                    )}
                </>
            )}

            {/* User Analytics - Only visible to admins and if feature enabled */}
            {isAdmin && isUserSearchEnabled && (
                <Grid item xs={12} md={gridSizes.analytics || gridSizes.default}>
                    <UserSearch
                        searchQuery={searchQuery}
                        setSearchQuery={handleSearchChange}
                        anchorEl={anchorEl}
                        setAnchorEl={setAnchorEl}
                        filteredUsers={filteredUsers}
                        isSearching={isSearching}
                        handleClickAway={handleClickAway}
                        handleSelectUser={handleSelectUser}
                        selectedUserId={selectedUserId}
                        userData={userData}
                        userDataLoading={userDataLoading}
                        theme={theme}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        refetchUserData={refetchUserData}
                    />
                </Grid>
            )}

            {/* Simulation Performance - Only visible to admins and if feature enabled */}
            {isAdmin && isSimulationPerformanceEnabled && (
                <Grid item xs={12} md={gridSizes.performance || gridSizes.default}>
                    <SimulationPerformance
                        simulationData={simulationData}
                        simulationDataLoading={simulationDataLoading}
                        theme={theme}
                        dateRange={simDateRange}
                        setDateRange={setSimDateRange}
                    />
                </Grid>
            )}

            {/* Department Overview - Conditionally rendered based on user type */}
            {isAdmin ? (
                <>
                    {isDepartmentOverviewEnabled && (
                        <Grid item xs={12} md={gridSizes.departmentOverview}>
                            <DepartmentOverview
                                tenancyId={activeTenancyId}
                                theme={theme}
                                user={!isAdmin ? fullUserData : null}
                                isAdmin={isAdmin}
                            />
                        </Grid>
                    )}
                </>
            ) : (
                <>
                    {isDepartmentOverviewEnabled && userHasDepartmentReporting && (
                        <Grid item xs={12} md={gridSizes.departmentOverview}>
                            <UserViewDepartmentOverview
                                tenancyId={activeTenancyId}
                                theme={theme}
                                user={fullUserData}
                            />
                        </Grid>
                        )}

                    {isDepartmentOverviewEnabled && userHasLineManagerReporting && (
                            <Grid item xs={12} md={gridSizes.departmentOverview}>
                                <UserViewLineManagerOverview
                                    tenancyId={activeTenancyId}
                                    theme={theme}
                                    user={fullUserData}
                                />
                            </Grid>
                    )}

                    {isDepartmentOverviewEnabled && userHasLocationReporting && (
                        <Grid item xs={12} md={gridSizes.departmentOverview}>
                            <UserViewLocationOverview
                                tenancyId={activeTenancyId}
                                theme={theme}
                                user={fullUserData}
                            />
                        </Grid>
                    )}
                </>
            )}

            {/* Show message if no components are enabled */}
            {!isCourseOverviewEnabled && !isActivityTimelineEnabled &&
                !(isAdmin && isUserSearchEnabled) &&
                !(isAdmin && isSimulationPerformanceEnabled) &&
                !(userHasDepartmentReporting && isDepartmentOverviewEnabled) &&
                !(userHasLocationReporting && isDepartmentOverviewEnabled) &&
                !(userHasLineManagerReporting && isDepartmentOverviewEnabled) && (
                    <Grid item xs={12}>
                        <MainCard>
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <h3>No dashboard components are currently enabled</h3>
                                <p>Please contact your administrator to enable dashboard features.</p>
                            </div>
                        </MainCard>
                    </Grid>
                )}

        </Grid>
    );
}